import React, { useState, useRef, useCallback } from 'react';
import SubMenuBar from './SubMenuBar';
import WaveformDisplay from './WaveformDisplay';
import { getItem, setItem } from '../utils/storage';
import {
  DEFAULT_GAIN,
  GLOBAL_GAIN_KEY,
  MIN_GAIN,
  MAX_GAIN,
} from '../utils/constants';
import { dlg_ShowDialog, DLG_MICROPHONE_PROBLEM } from '../utils/CommonDialogs';
import './MicrophoneHelp.css';
import * as Sentry from '@sentry/react';

function MicrophoneHelp() {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [gainValue, setGainValue] = useState(parseFloat(getItem(GLOBAL_GAIN_KEY, DEFAULT_GAIN)));
  const [error, setError] = useState(null);
  const [microphones, setMicrophones] = useState([]);
  const [selectedMic, setSelectedMic] = useState('');
  const [micsLoaded, setMicsLoaded] = useState(false);
  const audioContextRef = useRef(null);
  const analyzerRef = useRef(null);
  const gainNodeRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Find microphones only when user requests
  const handleFindMicrophones = async () => {
    setError(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const message = 'Your browser does not support microphone access (getUserMedia). Please use a modern browser.';
        setError(message);
        console.error(message);
        if (Sentry && Sentry.captureException) Sentry.captureException(new Error(message));
        setMicrophones([]);
        setMicsLoaded(false);
        setSelectedMic('');
        return;
      }
      // Request permission to enumerate devices
      await navigator.mediaDevices.getUserMedia({ audio: true });
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      setMicrophones(audioInputs);
      setMicsLoaded(true);
      if (audioInputs.length > 0) {
        setSelectedMic(audioInputs[0].deviceId);
      } else {
        setSelectedMic('');
      }
    } catch (err) {
      let message = 'Could not access microphones. Please check permissions.';
      if (err.name === 'NotAllowedError') {
        message = 'Microphone access was denied. Please allow access in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        message = 'No microphone found. Please connect a microphone and try again.';
      } else if (err.name === 'NotReadableError') {
        message = 'Microphone is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        message = 'The selected microphone is not available.';
      } else if (err.message && err.message.includes('timed out')) {
        message = 'Microphone setup timed out. Please try again or check your browser permissions.';
      }
      setError(message);
      console.error('Microphone error:', message, err);
      if (Sentry && Sentry.captureException) {
        const sentryError = new Error(message);
        sentryError.originalError = err;
        Sentry.captureException(sentryError);
      }
      dlg_ShowDialog(DLG_MICROPHONE_PROBLEM, {
        message: err && err.message ? err.message : String(err),
        errorCode: err && err.name ? err.name : 'UnknownError',
        forceShow: true
      });
      setMicrophones([]);
      setMicsLoaded(false);
      setSelectedMic('');
    }
  };

  // Audio setup for test
  const initializeAudio = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        const message = 'Your browser does not support microphone access (getUserMedia). Please use a modern browser.';
        setError(message);
        console.error(message);
        if (Sentry && Sentry.captureException) Sentry.captureException(new Error(message));
        return false;
      }
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
      } else if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      // IMPORTANT: For maximum hardware compatibility, we don't disable audio processing features
      // like echoCancellation, noiseSuppression, or autoGainControl.
      //
      // Some hardware, particularly USB headsets that combine microphone and headphone 
      // functionality, can have issues if these processing features are explicitly disabled.
      // By letting the browser use default processing, we ensure compatibility across a wider range
      // of devices at the cost of potentially less "pure" audio.
      const constraints = selectedMic ? { audio: { deviceId: { exact: selectedMic } } } : { audio: true };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      const source = audioContextRef.current.createMediaStreamSource(stream);
      // Bandpass filter
      const bandpass = audioContextRef.current.createBiquadFilter();
      bandpass.type = 'bandpass';
      bandpass.frequency.setValueAtTime(700, audioContextRef.current.currentTime);
      bandpass.Q.setValueAtTime(0.5, audioContextRef.current.currentTime);
      // Gain node
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.gain.setValueAtTime(gainValue, audioContextRef.current.currentTime);
      // Analyzer
      analyzerRef.current = audioContextRef.current.createAnalyser();
      analyzerRef.current.fftSize = 8192;
      analyzerRef.current.smoothingTimeConstant = 0.8;
      // Connect: source -> bandpass -> gain -> analyzer
      source.connect(bandpass);
      bandpass.connect(gainNodeRef.current);
      gainNodeRef.current.connect(analyzerRef.current);
      setError(null);
      return true;
    } catch (err) {
      let message = 'Failed to access microphone. Please check permissions.';
      if (err.name === 'NotAllowedError') {
        message = 'Microphone access was denied. Please allow access in your browser settings.';
      } else if (err.name === 'NotFoundError') {
        message = 'No microphone found. Please connect a microphone and try again.';
      } else if (err.name === 'NotReadableError') {
        message = 'Microphone is already in use by another application.';
      } else if (err.name === 'OverconstrainedError') {
        message = 'The selected microphone is not available.';
      } else if (err.message && err.message.includes('timed out')) {
        message = 'Microphone setup timed out. Please try again or check your browser permissions.';
      }
      setError(message);
      console.error('Microphone error:', message, err);
      if (Sentry && Sentry.captureException) {
        const sentryError = new Error(message);
        sentryError.originalError = err;
        Sentry.captureException(sentryError);
      }
      return false;
    }
  }, [gainValue, selectedMic]);

  // Cleanup audio
  const cleanup = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (analyzerRef.current) {
      analyzerRef.current.disconnect();
      analyzerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
  }, []);

  // Start/stop test
  const handleTestClick = async () => {
    if (isTesting) {
      setIsTesting(false);
      cleanup();
    } else {
      const ok = await initializeAudio();
      if (ok) setIsTesting(true);
    }
  };

  // Gain change
  const handleGainChange = (e) => {
    const value = parseFloat(e.target.value);
    const validated = Math.max(MIN_GAIN, Math.min(MAX_GAIN, value));
    setGainValue(validated);
    setItem(GLOBAL_GAIN_KEY, validated.toString());
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.setValueAtTime(validated, audioContextRef.current.currentTime);
    }
  };

  // Stop audio on unmount
  React.useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Platform detection for solution jump
  const getPlatformSectionId = () => {
    const ua = navigator.userAgent || navigator.vendor || window.opera;
    if (/windows phone/i.test(ua)) return 'windows';
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return 'ios';
    if (/Macintosh|MacIntel|MacPPC|Mac68K/.test(ua)) return 'macos';
    if (/Win32|Win64|Windows|WinCE/.test(ua)) return 'windows';
    if (/Linux/.test(ua)) return 'linux';
    return 'windows'; // Default
  };

  // Browser detection for solution jump
  const getBrowserId = () => {
    const ua = navigator.userAgent;
    if (/Edg\//.test(ua)) return 'edge';
    if (/SamsungBrowser\//.test(ua)) return 'samsung';
    if (/Chrome\//.test(ua) && !/Edg\//.test(ua) && !/OPR\//.test(ua)) return 'chrome';
    if (/Firefox\//.test(ua)) return 'firefox';
    if (/Safari\//.test(ua) && !/Chrome\//.test(ua) && !/Chromium\//.test(ua)) return 'safari';
    return null;
  };

  const handleHowToFix = () => {
    const platform = getPlatformSectionId();
    const browser = getBrowserId();
    let anchor = platform;
    if (browser) {
      anchor = `${platform}-${browser}`;
    }
    window.open(`/MicrophoneHelp.html#${anchor}`, '_blank', 'noopener,noreferrer');
  };

  const menus = {
    help: {
      label: 'Help Topics',
      items: [
        { label: 'Basic Setup', action: () => setShowAdvanced(false), selected: !showAdvanced },
        { label: 'Advanced Troubleshooting', action: () => setShowAdvanced(true), selected: showAdvanced },
      ],
    },
  };

  return (
    <div className="microphone-help-container">
      <SubMenuBar menus={menus} />
      <div className="microphone-help-content" style={{ color: 'white' }}>
        <h1>Microphone Help</h1>
        {!showAdvanced ? (
          <>
            <section className="microphone-help-section">
              <h2>Important</h2>
              <div>This app uses the microphone to analyze pitch for our games.You must allow microphone access when prompted by your browser.</div>
              <div>If you deny access to the microphone, you may have to correct this on your browser or system settings.</div>
            </section>
            <section className="microphone-help-section">
              <h2>Test Your Microphone</h2>
              <div
                className="microphone-help-audio-test-container"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  width: '30vw',
                  minWidth: 260,
                  maxWidth: 400,
                  margin: '0 auto',
                  alignItems: 'center',
                }}
              >
                <button
                  className="microphone-help-find-mics-button"
                  onClick={handleFindMicrophones}
                  style={{ marginBottom: 12 }}
                  disabled={isTesting}
                >
                  Find Microphones
                </button>
                <select
                  className="microphone-help-mic-select"
                  value={selectedMic}
                  onChange={e => setSelectedMic(e.target.value)}
                  disabled={!micsLoaded || microphones.length === 0 || isTesting}
                  style={{ marginBottom: 12, width: '100%' }}
                >
                  {microphones.length === 0 ? (
                    <option value="">No microphones found</option>
                  ) : (
                    microphones.map(mic => (
                      <option key={mic.deviceId} value={mic.deviceId}>
                        {mic.label || `Microphone ${mic.deviceId.slice(0, 5)}...`}
                      </option>
                    ))
                  )}
                </select>
                <button
                  className={`microphone-help-test-button${isTesting ? ' active' : ''}`}
                  onClick={handleTestClick}
                  disabled={!selectedMic || !micsLoaded}
                  style={{ marginBottom: 12 }}
                >
                  {isTesting ? 'Stop Test' : 'Start Test'}
                </button>
                <div className="microphone-help-gain-control" style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <label htmlFor="microphone-help-gain-slider" style={{ minWidth: 90, marginRight: 8 }}>
                    Input Gain
                  </label>
                  <input
                    id="microphone-help-gain-slider"
                    type="range"
                    min={MIN_GAIN}
                    max={MAX_GAIN}
                    step="0.1"
                    value={gainValue}
                    onChange={handleGainChange}
                    disabled={!isTesting}
                    style={{ flex: 1, margin: '0 8px' }}
                  />
                  <span style={{ minWidth: 32, textAlign: 'right' }}>{gainValue.toFixed(1)}</span>
                </div>
                {error && (
                  <button
                    className="microphone-help-error-fix-btn"
                    style={{ margin: '0.5rem 0', padding: '0.5rem 1rem', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    onClick={handleHowToFix}
                    aria-live="assertive"
                  >
                    How to Fix
                  </button>
                )}
                {error && (
                  <div role="alert" style={{ color: '#ff5252', margin: '8px 0', fontWeight: 500 }}>
                    {error}
                  </div>
                )}
                <div className="microphone-help-waveform" style={{ width: '100%' }}>
                  <h3>Input Signal</h3>
                  <WaveformDisplay analyzerNode={analyzerRef.current} isActive={isTesting} />
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <section className="microphone-help-section">
              <h2>Advanced Troubleshooting</h2>
              <div>Try a different browser (Chrome, Firefox, Edge, Safari).</div>
              <div>Ensure no other app is using the microphone exclusively.</div>
              <div>Check for browser updates and OS updates.</div>
              <div>Restart your device if issues persist.</div>
              <div>On Mac: Go to System Preferences &gt; Security & Privacy &gt; Microphone and allow access.</div>
              <div>On Windows: Go to Settings &gt; Privacy &gt; Microphone and allow access.</div>
              <div>Still stuck? Contact us via the <b>Contact</b> page for more help.</div>
              <button
                className="microphone-help-open-html-btn"
                style={{ marginTop: '1rem', padding: '0.5rem 1rem', background: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                onClick={() => window.open('/MicrophoneHelp.html', '_blank', 'noopener,noreferrer')}
              >
                Open Full Microphone Help Page
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}

export default MicrophoneHelp; 